import type { ReactNode } from "react";
import { platformBrand } from "../branding";
import { AlertTriangle, ArrowLeft, LoaderCircle, RefreshCw, ShieldCheck } from "lucide-react";
import "./system-state.css";

type SystemStateTone = "danger" | "warning" | "neutral" | "loading";

type SystemStateProps = {
  title: string;
  description: string;
  tone?: SystemStateTone;
  mode?: "page" | "inline" | "banner";
  icon?: ReactNode;
  primaryAction?: { label: string; onClick: () => void; disabled?: boolean };
  secondaryAction?: { label: string; onClick: () => void };
  reference?: string;
  assistance?: string;
  brand?: { name: string; description: string };
  referenceLabel?: string;
};

function DefaultIcon({ tone }: { tone: SystemStateTone }) {
  if (tone === "loading") return <LoaderCircle className="system-state-spinner" aria-hidden="true" />;
  if (tone === "danger" || tone === "warning") return <AlertTriangle aria-hidden="true" />;
  return <ShieldCheck aria-hidden="true" />;
}

export function SystemState({
  title,
  description,
  tone = "neutral",
  mode = "inline",
  icon,
  primaryAction,
  secondaryAction,
  reference,
  assistance,
  brand,
  referenceLabel = "İşlem kodu",
}: SystemStateProps) {
  const Heading = mode === "page" ? "h1" : "h2";
  const content = (
    <section
      className={`system-state system-state-${mode} is-${tone}`}
      role={tone === "danger" ? "alert" : "status"}
      aria-live={tone === "danger" ? "assertive" : "polite"}
      aria-busy={tone === "loading" || undefined}
    >
      {mode === "page" ? (
        <div className="system-state-brand" aria-label={brand?.name ?? platformBrand.name}>
          <img src="/anl-logo-new-2026.webp" alt="ANL" />
          <span><strong>{brand?.name ?? platformBrand.name}</strong><small>{brand?.description ?? platformBrand.description}</small></span>
          <p>Sistem durumu</p>
        </div>
      ) : null}
      <div className="system-state-panel">
        <div className="system-state-symbol">{icon ?? <DefaultIcon tone={tone} />}</div>
        <div className="system-state-copy">
          <Heading>{title}</Heading>
          <p>{description}</p>
        </div>
        {primaryAction || secondaryAction ? (
          <div className="system-state-actions">
            {primaryAction ? <button type="button" className="system-state-primary" disabled={primaryAction.disabled} onClick={primaryAction.onClick}><RefreshCw aria-hidden="true" />{primaryAction.label}</button> : null}
            {secondaryAction ? <button type="button" className="system-state-secondary" onClick={secondaryAction.onClick}><ArrowLeft aria-hidden="true" />{secondaryAction.label}</button> : null}
          </div>
        ) : null}
        {reference ? <p className="system-state-reference"><span>{referenceLabel}</span><code>{reference}</code></p> : null}
        {assistance ? <p className="system-state-assistance">{assistance}</p> : null}
      </div>
    </section>
  );

  return mode === "page" ? <main className={`system-state-shell is-${tone}`}>{content}</main> : content;
}
